import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRecoilState } from 'recoil';
import { Button, SelectDropdown, TextInput } from '@gsa/afp-component-library';
import { useLazyQuery } from '@apollo/client';
import VendorSearchTypeahead, {
  vendorTypeAheadValue,
} from './vendor-search-typeahead';
import { useVendorInfo } from '../../apis/vendor-api';
import { GET_BIDS_BY_SOLI_NUMBER } from './queries.gql';
import { bidDashboardAdmivDefaultViewAtom } from '../../../atoms/bids-atoms';
import useHistoryState from '../../../../../hooks/use-history-state';

const AdminBidSearchPanel = ({ onBidSearchCompletion }) => {
  const [, setSearchOpt] = useRecoilState(bidDashboardAdmivDefaultViewAtom);
  const { setVendorInfoForAdmin } = useVendorInfo();
  // The last search run on this page, kept on the history entry so the
  // browser back button brings the results back (breadcrumbs start clean).
  const [lastSearch, setLastSearch] = useHistoryState('bidSearch', null);
  const [selectedBidSearchOption, setSelectedBidSearchOption] = useState(
    lastSearch?.option ?? '',
  );
  const [solicitationNumber, setSolicitationNumber] = useState(
    lastSearch?.solicitationNumber ?? '',
  );
  const [
    bidSearchOptionInputErrorMessage,
    setBidSearchOptionInputErrorMessage,
  ] = useState(null);
  const [
    solicitationNumInputErrorMessage,
    setSolicitationNumInputErrorMessage,
  ] = useState('');
  const [showSearchResultText, setShowSearchResultText] = useState({
    show: false,
    text: '',
  });
  const [
    vendorTypeaheadSearchResult,
    setVendorTypeaheadSearchResult,
  ] = useState(lastSearch?.vendor ?? null);

  const [getBidsBySolicitationNumber] = useLazyQuery(GET_BIDS_BY_SOLI_NUMBER, {
    fetchPolicy: 'network-only',
    onCompleted: ({ getBidsBySolicitationNumber: data }) => {
      onBidSearchCompletion(data);
      setShowSearchResultText({ show: true, text: solicitationNumber });
    },
    onError: () => {
      setShowSearchResultText({
        show: true,
        text: 'Not able to fetch bids data',
      });
    },
  });

  useEffect(() => {
    // The selected view lives in a recoil atom that outlives this page, so
    // set it from this history entry: the restored search, or none at all.
    setSearchOpt(lastSearch?.option ?? null);
    if (lastSearch?.option === 'Solicitation number') {
      getBidsBySolicitationNumber({
        variables: { solicitationNumber: lastSearch.solicitationNumber },
      });
    }
    if (lastSearch?.option === 'Vendor name/UEI') {
      setVendorInfoForAdmin(lastSearch.vendor);
      setShowSearchResultText({
        show: true,
        text: lastSearch.vendor?.vendorName,
      });
    }
  }, []);

  const handleVendorSelection = (data) => {
    const { id, uniqueEntityIdentifier, vendorName } = data || {};
    setVendorTypeaheadSearchResult({
      id,
      uniqueEntityIdentifier,
      vendorName,
    });
  };

  const handleSearch = () => {
    if (!solicitationNumber || solicitationNumber === '') {
      setSolicitationNumInputErrorMessage('This field is required.');
    }
    if (!selectedBidSearchOption || selectedBidSearchOption === '') {
      setBidSearchOptionInputErrorMessage('This field is required.');
    }

    if (solicitationNumber !== '' && selectedBidSearchOption !== '') {
      // reset
      setSolicitationNumInputErrorMessage('');
      setBidSearchOptionInputErrorMessage('');

      if (selectedBidSearchOption === 'Solicitation number') {
        setLastSearch({ option: selectedBidSearchOption, solicitationNumber });
      }

      // call search bid by soli number
      getBidsBySolicitationNumber({
        variables: {
          solicitationNumber,
        },
      });
    }

    if (
      selectedBidSearchOption === 'Vendor name/UEI' &&
      vendorTypeaheadSearchResult
    ) {
      setVendorInfoForAdmin(vendorTypeaheadSearchResult);
      setLastSearch({
        option: selectedBidSearchOption,
        vendor: vendorTypeaheadSearchResult,
      });
      setShowSearchResultText({
        show: true,
        text: vendorTypeaheadSearchResult?.vendorName,
      });
    }
  };

  const searchBidByOptions = [
    {
      label: '-Select-',
      value: '',
    },
    {
      label: 'Vendor name/UEI',
      value: 'Vendor name/UEI',
    },
    {
      label: 'Solicitation number',
      value: 'Solicitation number',
    },
  ];

  const handleBidSearchOptionChange = (value) => {
    // reset
    setSolicitationNumInputErrorMessage('');
    setBidSearchOptionInputErrorMessage('');
    onBidSearchCompletion([]);
    setSolicitationNumber('');
    setShowSearchResultText({ show: false, text: '' });
    setSelectedBidSearchOption(value);
    setVendorTypeaheadSearchResult({});
    setVendorInfoForAdmin({});
    setSearchOpt(value);
  };

  return (
    <>
      <div className="bg-primary-lightest padding-bottom-6 padding-top-4 margin-bottom-6">
        <div className="margin-2 grid-row">
          <div className="grid-col-3">
            <div className="padding-left-3 width-card-lg">
              <SelectDropdown
                name="search-bid-by"
                required
                id="search-bid-by"
                data-testid="search-bid-by"
                help="Select"
                value={selectedBidSearchOption}
                onChange={(ev) => handleBidSearchOptionChange(ev.target.value)}
                options={searchBidByOptions}
                label="Search by"
                errorMessage={
                  bidSearchOptionInputErrorMessage &&
                  bidSearchOptionInputErrorMessage
                }
              />
            </div>
          </div>
          <div className="grid-col-3">
            <div className="width-auto">
              {selectedBidSearchOption === 'Solicitation number' ? (
                <TextInput
                  name="solicitation-number"
                  id="solicitation-number"
                  data-testid="solicitation-number"
                  type="text"
                  label={`Search for a bid by ${selectedBidSearchOption}`}
                  value={solicitationNumber}
                  onChange={(e) => {
                    setSolicitationNumber(e.target.value);
                    setSolicitationNumInputErrorMessage('');
                  }}
                  errorMessage={solicitationNumInputErrorMessage}
                />
              ) : (
                <VendorSearchTypeahead
                  onVendorSelection={handleVendorSelection}
                  label={selectedBidSearchOption}
                  initialValue={
                    lastSearch?.vendor?.vendorName
                      ? vendorTypeAheadValue(
                          lastSearch.vendor.vendorName,
                          lastSearch.vendor.uniqueEntityIdentifier,
                        )
                      : ''
                  }
                />
              )}
            </div>
          </div>
          <div className="grid-col-3">
            <div className="padding-top-7">
              <Button
                name="search-bid-for-admin"
                data-testid="search-bid-for-admin"
                onClick={() => handleSearch()}
                type="submit"
                variant="outline"
                label="Search"
                leftIcon={{ name: 'search' }}
                className="margin-left-2 margin-right-0"
              />
            </div>
          </div>
          <div className="grid-col-3" />
        </div>
      </div>
      {showSearchResultText.show && (
        <div className="margin-bottom-8 margin-top-neg-2">
          <span className="text-bold">
            Search Results for {selectedBidSearchOption} &quot;{' '}
            {showSearchResultText.text} &quot;
          </span>
        </div>
      )}
    </>
  );
};

AdminBidSearchPanel.propTypes = {
  onBidSearchCompletion: PropTypes.func.isRequired,
};

export default AdminBidSearchPanel;
